<template>
  <section class="px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto max-w-7xl">
      <div class="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6 lg:p-8">
        <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-center">
          <div class="flex min-w-0 items-start gap-3">
            <HistoryBackButton />
            <div class="min-w-0">
              <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Support</p>
              <h1 class="mt-2 text-3xl font-bold text-slate-950 sm:text-4xl">How can we help?</h1>
              <p class="mt-3 max-w-2xl text-sm font-medium leading-6 text-slate-500 sm:text-base">
                Contact the NetFlyr team about accounts, flyers, saved posts, reports, or product questions.
              </p>
            </div>
          </div>

          <div class="grid gap-3 rounded-[1.5rem] bg-slate-100 p-4">
            <div
              v-for="item in contactCards"
              :key="item.label"
              class="flex items-center gap-3 rounded-2xl bg-white px-4 py-3"
            >
              <span class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xl text-blue-700">
                <i :class="item.icon" aria-hidden="true"></i>
              </span>
              <div class="min-w-0">
                <p class="m-0 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{{ item.label }}</p>
                <p class="m-0 truncate text-sm font-bold text-slate-950">{{ item.value }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <form class="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6" @submit.prevent="submitSupportRequest">
          <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
            <div>
              <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Message</p>
              <h2 class="mt-2 text-2xl font-bold text-slate-950">Send a request</h2>
            </div>
            <span class="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
              Usually 1-2 days
            </span>
          </div>

          <div class="mt-5 grid gap-4 sm:grid-cols-2">
            <label class="grid gap-1.5 text-sm font-bold text-slate-600">
              <span>Email</span>
              <input
                v-model.trim="form.email"
                class="min-h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                type="email"
                placeholder="you@example.com"
                required
              />
            </label>

            <label class="grid gap-1.5 text-sm font-bold text-slate-600">
              <span>Topic</span>
              <select
                v-model="form.topic"
                class="min-h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-950 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
              >
                <option v-for="topic in topics" :key="topic" :value="topic">{{ topic }}</option>
              </select>
            </label>

            <label class="grid gap-1.5 text-sm font-bold text-slate-600 sm:col-span-2">
              <span>Subject</span>
              <input
                v-model.trim="form.subject"
                class="min-h-12 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                type="text"
                placeholder="What do you need help with?"
                required
              />
            </label>

            <label class="grid gap-1.5 text-sm font-bold text-slate-600 sm:col-span-2">
              <span>Message</span>
              <textarea
                v-model.trim="form.message"
                class="min-h-36 resize-y rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium leading-6 text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                placeholder="Share the flyer title, account email, or steps that caused the issue."
                required
              ></textarea>
            </label>
          </div>

          <div class="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              class="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-blue-600 px-5 text-sm font-bold text-white transition hover:bg-blue-700"
              type="submit"
            >
              <i class="ri-send-plane-fill text-base" aria-hidden="true"></i>
              Send Message
            </button>
            <p v-if="sent" class="m-0 text-sm font-bold text-emerald-700">
              Support request prepared for backend integration.
            </p>
          </div>
        </form>

        <aside class="grid content-start gap-4">
          <div class="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
            <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Quick Help</p>
            <h2 class="mt-2 text-xl font-bold text-slate-950">Common topics</h2>

            <div class="mt-4 grid gap-3">
              <button
                v-for="item in quickHelp"
                :key="item.title"
                class="flex w-full items-start gap-3 rounded-2xl border border-slate-200 bg-[#fcfbf8] p-4 text-left transition hover:border-slate-300 hover:bg-white"
                type="button"
                @click="selectTopic(item)"
              >
                <span class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-lg text-blue-700">
                  <i :class="item.icon" aria-hidden="true"></i>
                </span>
                <span class="min-w-0">
                  <span class="block text-sm font-bold text-slate-950">{{ item.title }}</span>
                  <span class="mt-1 block text-xs font-medium leading-5 text-slate-500">{{ item.copy }}</span>
                </span>
              </button>
            </div>
          </div>

          <div class="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
            <p class="m-0 text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Before You Send</p>
            <ul class="mt-4 grid list-none gap-3 p-0 text-sm font-medium leading-6 text-slate-500">
              <li v-for="item in checklist" :key="item" class="flex gap-2">
                <i class="ri-checkbox-circle-fill mt-0.5 text-base text-emerald-600" aria-hidden="true"></i>
                <span>{{ item }}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'

const sent = ref(false)
const topics = ['Account', 'Flyer', 'Saved Flyers', 'Report', 'Billing', 'Other']
const form = ref({
  email: '',
  topic: 'Account',
  subject: '',
  message: ''
})

const contactCards = [
  { label: 'Email', value: 'support@netflyr.com', icon: 'ri-mail-line' },
  { label: 'Availability', value: 'Mon-Fri support', icon: 'ri-time-line' },
  { label: 'Response', value: 'Usually 1-2 days', icon: 'ri-customer-service-2-line' }
]

const quickHelp = [
  { title: 'Account access', topic: 'Account', icon: 'ri-user-settings-line', copy: 'Profile, login, password, or account settings.' },
  { title: 'Flyer issues', topic: 'Flyer', icon: 'ri-file-list-3-line', copy: 'Posting, editing, images, locations, or visibility.' },
  { title: 'Saved flyers', topic: 'Saved Flyers', icon: 'ri-bookmark-line', copy: 'Bookmarks, collections, or missing saved posts.' },
  { title: 'Report content', topic: 'Report', icon: 'ri-flag-line', copy: 'Tell us about inaccurate, harmful, or spam content.' }
]

const checklist = [
  'Include the flyer title or link when the issue is about a specific post.',
  'Use the email connected to your NetFlyr account.',
  'Mention what device or browser you used if something is not loading.'
]

const selectTopic = (item) => {
  form.value.topic = item.topic
  if (!form.value.subject) {
    form.value.subject = item.title
  }
}

const submitSupportRequest = () => {
  sent.value = true
}
</script>
